import type { GoalStatus, PlayerGoal } from "@/lib/types";
import {
  ACTIVE_GOAL_STATUSES,
  GOAL_STATUS_OPTIONS,
  goalContextLabel,
  goalPriorityLabel,
  goalStatusLabel,
  goalVisibilityLabel,
} from "@/features/coach/goalLabels";

/**
 * Player goal card — shared between the coach Player Development
 * modal and the viewer Development sub-tab. Ports the legacy
 * `_renderGoalCard` (`js/coaching/goals.js`).
 *
 * `viewer` gates what shows:
 *   - coach: visibility/priority/target-date meta line +
 *     `coach_private_note` block + a status dropdown + Edit +
 *     Delete actions.
 *   - viewer: none of the coach meta, no `coach_private_note`
 *     EVER, and a single "Add reflection" action.
 *
 * Privacy: `coach_private_note` is templated ONLY when
 * `!viewer`. The viewer payload already scrubs it server-side —
 * this `!viewer` guard is the defense-in-depth client layer.
 *
 * Presentational — actions are delegated to the host via
 * callbacks. `actions={false}` renders a read-only card (the
 * reflection modal embeds one that way).
 */

export type GoalCardProps = {
  goal: PlayerGoal;
  viewer: boolean;
  /** `false` ⇒ no action row at all (read-only embed). Default true. */
  actions?: boolean;
  // --- coach actions (ignored when `viewer`) ---
  onChangeStatus?: (goalId: number, status: GoalStatus) => void;
  onEdit?: (goal: PlayerGoal) => void;
  onDelete?: (goal: PlayerGoal) => void;
  // --- viewer action (ignored when `!viewer`) ---
  onAddReflection?: (goal: PlayerGoal) => void;
};

export function GoalCard({
  goal,
  viewer,
  actions = true,
  onChangeStatus,
  onEdit,
  onDelete,
  onAddReflection,
}: GoalCardProps) {
  const status = goal.status || "open";
  const isActive = ACTIVE_GOAL_STATUSES.has(status);
  const latest = goal.latest_reflection ?? goal.reflections?.[0] ?? null;

  // Coach-only meta line: visibility · priority · target date.
  const coachMeta = !viewer
    ? [
        goalVisibilityLabel(goal.visibility),
        `${goalPriorityLabel(goal.priority)} priority`,
        goal.target_date ? `Target ${goal.target_date}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  // Linked source summary (note / clip / playlist), if hydrated.
  const source = goalSourceSummary(goal);

  return (
    <article
      data-testid="goal-card"
      data-goal-id={goal.id}
      className={[
        "rounded-lg border border-rule bg-bg-card px-3 py-2",
        isActive ? "" : "opacity-60",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {goalContextLabel(goal.context)}
          </span>
          <h4 className="font-display text-ink truncate">
            {goal.title || "Player goal"}
          </h4>
          {coachMeta ? (
            <span className="text-ink-muted text-xs">{coachMeta}</span>
          ) : null}
        </div>
        <span
          data-goal-status={status}
          className="shrink-0 rounded px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink"
        >
          {goalStatusLabel(status)}
        </span>
      </div>

      {goal.description ? (
        <GoalBlock label="Action plan" text={goal.description} />
      ) : null}
      {goal.success_criteria ? (
        <GoalBlock label="Success criteria" text={goal.success_criteria} />
      ) : null}

      {/* Coach-only — never templated on the viewer surface. */}
      {!viewer && goal.coach_private_note ? (
        <div
          data-testid="goal-card-private-note"
          className="mt-2 rounded border border-rule/60 bg-surface/40 px-2 py-1.5"
        >
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Coach private note
          </span>
          <p className="text-ink text-sm mt-0.5">
            {goal.coach_private_note}
          </p>
        </div>
      ) : null}

      {source ? (
        <p className="text-ink-muted text-xs mt-2">
          {source.label}: <strong className="text-ink">{source.text}</strong>
        </p>
      ) : null}

      {latest ? (
        <div
          data-testid="goal-card-latest-reflection"
          className="mt-2 rounded border border-rule/60 bg-surface/40 px-2 py-1.5"
        >
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Latest reflection
          </span>
          <p className="text-ink text-sm mt-0.5">{latest.reflection}</p>
        </div>
      ) : null}

      {actions ? (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {viewer ? (
            <button
              type="button"
              onClick={() => onAddReflection?.(goal)}
              data-testid="goal-card-add-reflection"
              className="bg-accent hover:bg-accent-hover text-white rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
            >
              Add reflection
            </button>
          ) : (
            <>
              <select
                value={status}
                onChange={(e) =>
                  onChangeStatus?.(goal.id, e.target.value as GoalStatus)
                }
                aria-label="Goal status"
                data-testid="goal-card-status-select"
                className="tktic-select text-xs"
              >
                {GOAL_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onEdit?.(goal)}
                data-testid="goal-card-edit"
                className="border border-rule text-ink-muted hover:text-ink hover:bg-surface rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => onDelete?.(goal)}
                data-testid="goal-card-delete"
                className="border border-rule text-destructive hover:bg-surface rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
              >
                Delete
              </button>
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}

function GoalBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-2">
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <p className="text-ink text-sm mt-0.5">{text}</p>
    </div>
  );
}

/** A `{label, text}` summary of a goal's linked source — the
 * hydrated note / clip / playlist (already visibility-filtered +
 * scrubbed server-side). `null` if no source is hydrated. */
function goalSourceSummary(
  goal: PlayerGoal,
): { label: string; text: string } | null {
  if (goal.source_note) {
    return {
      label: "Source note",
      text:
        goal.source_note.title?.trim() ||
        goal.source_note.event_title?.trim() ||
        `Note #${goal.source_note.id}`,
    };
  }
  if (goal.source_clip) {
    return {
      label: "Source clip",
      text: goal.source_clip.title?.trim() || `Clip #${goal.source_clip.id}`,
    };
  }
  if (goal.source_playlist) {
    return {
      label: "Source playlist",
      text:
        goal.source_playlist.title?.trim() ||
        `Playlist #${goal.source_playlist.id}`,
    };
  }
  return null;
}
